import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, Edit, Delete, Functions, DragHandle } from '@mui/icons-material';
import axios from 'axios';

import { API_BASE_URL } from '../../../config/api';

const FormulaComponentsTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    componentName: '',
    componentType: 'Addition',
    formula: '',
    displayOrder: 0,
    isActive: true,
    description: '',
  });

  const componentTypes = ['Addition', 'Multiplier', 'Percentage'];

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        componentName: item.componentName,
        componentType: item.componentType,
        formula: item.formula,
        displayOrder: item.displayOrder,
        isActive: item.isActive,
        description: item.description || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        componentName: '',
        componentType: 'Addition',
        formula: '',
        displayOrder: 0,
        isActive: true,
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/formula-components/${editingItem.id}`,
          formData
        );
        onSuccess('Formula component updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/formula-components`, formData);
        onSuccess('Formula component created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this formula component?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/formula-components/${id}`);
        onSuccess('Formula component deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  const getComponentTypeColor = (type) => {
    const colors = {
      'Addition': 'success',
      'Multiplier': 'warning',
      'Percentage': 'info'
    };
    return colors[type] || 'default';
  };

  // Sort data by displayOrder
  const sortedData = [...data].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Formula Components Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Formula Component
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}>Order</TableCell>
              <TableCell>Component Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Formula</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragHandle fontSize="small" color="action" />
                    <Typography>{item.displayOrder}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Functions fontSize="small" color="action" />
                    <Typography fontWeight="medium">{item.componentName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.componentType}
                    color={getComponentTypeColor(item.componentType)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'grey.100',
                      p: 0.5,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}
                  >
                    {item.formula}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Formula Component' : 'Add Formula Component'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Component Name"
              value={formData.componentName}
              onChange={(e) =>
                setFormData({ ...formData, componentName: e.target.value })
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Component Type</InputLabel>
              <Select
                value={formData.componentType}
                label="Component Type"
                onChange={(e) =>
                  setFormData({ ...formData, componentType: e.target.value })
                }
              >
                {componentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Formula"
              value={formData.formula}
              onChange={(e) =>
                setFormData({ ...formData, formula: e.target.value })
              }
              fullWidth
              helperText="Mathematical expression for this component (e.g., 'basePrice * 1.1')"
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) =>
                setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
              }
              inputProps={{ min: 0 }}
              fullWidth
              helperText="Order in which this component is applied in the formula"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              fullWidth
              helperText="Explain what this component does"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormulaComponentsTab;
